import React from "react";
import SearchableDropdown from "../SearchableDropdown/SearchableDropdown";
import { SearchableDropdownOption } from "../SearchableDropdown/types";
import { PhoneNumberCountryCodeSelectorProps } from "./utils/types";
import CountryOptionContent from "./components/CountryOptionContent";
import CountrySelectedContent from "./components/CountrySelectedContent";

const PhoneNumberCountryCodeSelector: React.FC<
  PhoneNumberCountryCodeSelectorProps
> = ({
  label,
  labelClassName = "",
  required,
  error,
  message,
  errorMessageClassName = "",
  placeholder = "Enter phone number",
  containerClassName = "",
  disabled,
  onBlur,
  name,
  countryOptions,
  selectedCountry,
  onCountryChange,
  phoneInput,
  onPhoneInputChange,
  validationError,
  countryDropdownClassName = "",
  phoneInputClassName = "",
  countryDropdownPlaceholder = "Country",
  countryDropdownSearchPlaceholder = "Search countries...",
  countryDropdownSectionHeading = "All Country codes",
  countryDropdownTriggerClassName = "",
  countryDropdownSelectedOptionClassName = "",
}) => {
  const searchableDropdownOptions: SearchableDropdownOption[] =
    countryOptions.map((country) => ({
      value: country.value,
      content:
        selectedCountry?.value === country.value ? (
          <CountrySelectedContent country={country} />
        ) : (
          <CountryOptionContent country={country} />
        ),
      label: `${country.name} ${country.dialCode}`,
    }));

  const currentError = error || !!validationError;
  const currentMessage = message || validationError;

  const inputId =
    name || `phone-input-${Math.random().toString(36).slice(2, 11)}`;
  const errorId = `${inputId}-error`;

  return (
    <div className={`flex flex-col gap-1 ${containerClassName}`}>
      {label && (
        <label htmlFor={inputId} className={`${labelClassName}`}>
          {label}
          {required && <span>*</span>}
        </label>
      )}

      <div className="flex gap-2">
        <SearchableDropdown
          options={searchableDropdownOptions}
          value={selectedCountry?.value || null}
          onChange={(countryValue) => {
            const country = countryOptions.find(
              (c) => c.value === countryValue
            );
            if (country) {
              onCountryChange(country);
            }
          }}
          placeholder={countryDropdownPlaceholder}
          disabled={disabled}
          searchPlaceholder={countryDropdownSearchPlaceholder}
          showSearch={true}
          enableApiSearch={false}
          sectionHeading={countryDropdownSectionHeading}
          className="w-fit"
          triggerClassName={`w-fit ${countryDropdownTriggerClassName}`}
          selectedOptionContainerClassName={`${countryDropdownSelectedOptionClassName}`}
          dropdownClassName={
            countryDropdownClassName || "w-[250px] min-w-[250px] max-h-[250px]"
          }
        />

        <div className="flex-1">
          <input
            id={inputId}
            type="tel"
            value={phoneInput}
            onChange={onPhoneInputChange}
            onBlur={onBlur}
            placeholder={placeholder}
            disabled={disabled}
            name={name}
            aria-invalid={currentError ? "true" : "false"}
            aria-describedby={
              currentError && currentMessage ? errorId : undefined
            }
            className={`w-full px-[8px] py-[6px] rounded-[6px] bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk outline-none font-normal text-md text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder ${phoneInputClassName}`}
          />
        </div>
      </div>

      {currentError && currentMessage && (
        <div
          id={errorId}
          role="alert"
          className={`text-xxs text-verba-red-alert dark:text-verba-red-alert-dk ${errorMessageClassName}`}
        >
          <span>{currentMessage}</span>
        </div>
      )}
    </div>
  );
};

export default PhoneNumberCountryCodeSelector;
